
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import Hey from "@/lib/hey-utils";
import { trackTripCardAction } from "@/utils/analytics";

export const useWeatherData = (tripId: string, destinationCode: string | null, destinationName: string) => {
  const [showWeather, setShowWeather] = useState(false);
  const [weatherData, setWeatherData] = useState<any>(null);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleWeatherClick = async () => {
    try {
      setShowWeather(true);
      trackTripCardAction("weather", tripId, destinationName);
      console.debug('================================================');
      console.debug('🌤️ STARTING WEATHER DATA FETCH');
      console.debug('📍 Trip ID:', tripId);
      
      if (!destinationCode) {
        console.error('No destination found to fetch weather');
        toast({
          variant: "destructive",
          description: "Could not find destination information"
        });
        return;
      }
      
      console.debug('🛬 Using identified destination airport code:', destinationCode);
      try {
        console.debug('🌐 Calling Hey.HeyUtils.getInstance().getWeatherByAirportCode with code:', destinationCode);
        console.debug('🔑 User authenticated:', Hey.HeyUtils.getInstance().isUserAuthenticated());
        console.debug('🔑 Tech token available:', !!Hey.HeyUtils.getInstance().techToken);
        
        const response = await Hey.HeyUtils.getInstance().getWeatherByAirportCode(destinationCode);
        console.debug('🌡️ Weather data fetched successfully:', response);
        console.debug('🌡️ Weather data type:', typeof response);
        console.debug('🌡️ Weather data keys:', Object.keys(response));
        
        // Check if the response has the expected structure
        if (response?.data?.Headline) {
          console.debug('✅ Processing weather data - has required properties');
          console.debug('📊 Headline:', response.data.Headline);
          
          if (response.data.DailyForecasts) {
            console.debug('📊 Daily forecasts count:', response.data.DailyForecasts.length);
          }
          
          // Set the data directly without transforming it
          // This allows us to access both old and new format in components
          setWeatherData({
            data: response.data,
            headline: {
              Text: response.data.Headline.Text
            }
          });
        } else {
          console.error('❌ Invalid weather data format - missing required properties');
          console.error('❌ Has Headline:', !!response?.data?.Headline);
          console.error('❌ Has DailyForecasts:', !!response?.data?.DailyForecasts);
          console.error('❌ Response structure:', JSON.stringify(response, null, 2));
          throw new Error('Invalid weather data format');
        }
      } catch (error) {
        console.error('❌ Error calling API:', error);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error stack:', error.stack);
        toast({
          variant: "destructive",
          description: "Failed to fetch weather data"
        });
      }
    } catch (error) {
      console.error('General error in weather handling:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);
      toast({
        variant: "destructive",
        description: "An error occurred while fetching weather data"
      });
    }
  };

  return {
    showWeather,
    setShowWeather,
    weatherData,
    handleWeatherClick
  };
};
